import { Card, List, Typography } from 'antd';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './BaseEntityList.scss';
import { markGUIReady } from 'containers/Guide/store/guide/actions';
import { t } from 'helpers/i18n';
import {
  createAddEntityItem,
  createEntityItem,
  ListEntityItemType,
  EntityItem,
  EntityListItem,
  BaseEntityItem,
} from './Model';
import BaseAddEntityPopup, {
  EntityDesc,
} from '../BaseAddEntityPopup/BaseAddEntityPopup';
import { Result } from 'util/result';
import { BaseResponse } from 'data/terra/api/terra/base/BaseResponse';

interface AddEntityBlockProps {
  entityName: string;
  onAddEntity: () => void;
  padding: string;
}

function AddEntityBlock(props: AddEntityBlockProps) {
  return (
    <Card
      hoverable
      cover={
        <img alt="" src="images/add.png" style={{ padding: props.padding }} />
      }
      onClick={() => {
        props.onAddEntity();
      }}
    >
      <Card.Meta
        title={t(`Add ${props.entityName}`)}
        description={t(`Add ${props.entityName} Description`)}
      />
    </Card>
  );
}

interface EntityBlockProps<T extends BaseEntityItem> {
  entity: T;
  onEntitySelected: (item: T) => void;
  padding: string;
}

function EntityBlock<T extends BaseEntityItem>(props: EntityBlockProps<T>) {
  return (
    <Card
      hoverable
      cover={
        <img
          alt=""
          src={randomImage(props.entity.name)}
          style={{ padding: props.padding }}
        />
      }
      onClick={() => {
        props.onEntitySelected(props.entity);
      }}
    >
      <Card.Meta
        title={props.entity.name}
        description={<div className="entity-extra">{props.entity.code}</div>}
      />
    </Card>
  );
}

export interface ListEntityProps<T extends BaseEntityItem> {
  entityName: string;
  entities: T[];
  itemsPerRow: number;
  onEntitySelected: (item: T) => void;
  onSaveEntity?: (data: EntityDesc) => Promise<Result<BaseResponse<T, void>>>;
  onSaveEntityResult?: (result: Result<BaseResponse<T, void>>) => void;
}

function ListEntity<T extends BaseEntityItem>(props: ListEntityProps<T>) {
  const dispatch = useDispatch();
  const [isAddingEntity, setIsAddingEntity] = React.useState(false);

  useEffect(() => {
    dispatch(markGUIReady([`#homePageList${props.entityName}s`]));
  }, [dispatch, props.entityName]);

  let items: EntityListItem[] = props.entities.map((p, idx) =>
    createEntityItem<T>(String(idx), p)
  );
  if (props.onSaveEntity && props.onSaveEntityResult) {
    items = [createAddEntityItem(), ...items];
  }

  const renderItem = (item: EntityListItem) => {
    switch (item.type) {
      case ListEntityItemType.Entity:
        return (
          <EntityBlock<T>
            entity={(item as EntityItem<T>).entity}
            onEntitySelected={props.onEntitySelected}
            padding={getEntityBlockPadding(props.itemsPerRow)}
          />
        );
      case ListEntityItemType.AddEntity:
        return (
          <AddEntityBlock
            entityName={props.entityName}
            onAddEntity={() => {
              setIsAddingEntity(true);
            }}
            padding={getAddEntityBlockPadding(props.itemsPerRow)}
          />
        );
      default:
        return undefined;
    }
  };

  return (
    <div
      className="list-entity-container"
      id={`homePageList${props.entityName}s`}
    >
      <Typography.Title>{props.entityName}</Typography.Title>
      <List
        grid={{ gutter: 16, column: props.itemsPerRow }}
        dataSource={items}
        renderItem={(item: EntityListItem) => (
          <List.Item>{renderItem(item)}</List.Item>
        )}
      />
      {isAddingEntity && props.onSaveEntity && props.onSaveEntityResult && (
        <BaseAddEntityPopup<T>
          popupTitle={t(`Add ${props.entityName}`)}
          onSaveEntity={props.onSaveEntity}
          onSaveEntityResult={props.onSaveEntityResult}
          handleCloseModal={() => {
            setIsAddingEntity(false);
          }}
        />
      )}
    </div>
  );
}

export default ListEntity;

const randomImage = (id: string) => {
  const sources = [
    'images/01_twiter.png',
    'images/02_facebook.png',
    'images/03_youtube.png',
    'images/14_google.png',
    'images/18_evernote.png',
  ];
  const idx = Math.abs(hashString(id)) % sources.length;
  return sources[idx];
};

const hashString = (src: string) => {
  var hash = 0,
    i,
    chr;
  if (src.length === 0) return hash;
  for (i = 0; i < src.length; i++) {
    chr = src.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

const getEntityBlockPadding = (itemsPerRow: number) => {
  switch (itemsPerRow) {
    case 4:
      return '64px';
    case 6:
      return '48px';
    default:
      return '48px';
  }
};

const getAddEntityBlockPadding = (itemsPerRow: number) => {
  switch (itemsPerRow) {
    case 4:
      return '96px';
    case 6:
      return '64px';
    default:
      return '64px';
  }
};
